import React from 'react'
import { connect } from 'react-redux'
import Icon from '@chegg-wt/core/components/Icon'

import { Config } from '@chegg-wt/core/config'
import { useRoutesContext } from '@chegg-wt/core/components/RoutesContext'
import { focusElement } from '@chegg-wt/core/utils/focusElement'
import { useModalBehavior } from '@chegg-ui/modal/'

import { routeToHref } from '@chegg-wt/core/utils/routes'
import {
  origins,
  UserClickPayload,
} from '@chegg-wt/core/state/instrumentation/rio/clickstreamInteractions'
import { userClick } from '@chegg-wt/core/state/instrumentation/actions'
import { closeModal } from '@chegg-wt/core/state/modals/actions'
import {
  modalTypeSelector,
  modalDataSelector,
} from '@chegg-wt/core/state/modals/selectors'
import Modal from '..'
import * as Styled from './styled'

const COPY_SUCCESS_MODAL_ID = 'copy-success-modal'

export interface Props {
  modalData: {
    text: string
    shouldShowWarning: boolean
    returnFocusSelector: string
  }
  closeModal: (name: string) => void
  modalName: string
  userClick: (payload: UserClickPayload) => void
}

export const CopySuccess: React.FunctionComponent<Props> = ({
  modalData,
  closeModal,
  modalName,
  userClick,
}) => {
  const { text, shouldShowWarning, returnFocusSelector } = modalData
  const { toPlagiarism } = useRoutesContext()

  const onClose = () => {
    closeModal(modalName)
    focusElement(returnFocusSelector, 0)
  }

  const { modalRef, onModalKeyDown } = useModalBehavior({
    isOpen: true,
    onClose,
    id: COPY_SUCCESS_MODAL_ID,
  })
  React.useEffect(() => {
    modalRef.current?.focus()
  }, [modalRef])

  const onClickCheckPaper = () => {
    userClick({ origin: origins.checkPaperAfterCopy })
    if (Config.shouldOpenInNewTab) {
      window.open(routeToHref(toPlagiarism()), '_blank')
    } else {
      window.location.href = routeToHref(toPlagiarism())
    }
  }

  return (
    <Modal
      ref={modalRef}
      onKeyDown={onModalKeyDown}
      closeButton
      enableScroll={false}
      fullMobileModal
      outsideClick
      showModal
      maxWidth="290px"
      showBackdrop
      onClose={() => {
        focusElement(returnFocusSelector, 0)
      }}
      id={COPY_SUCCESS_MODAL_ID}
    >
      <Styled.Message data-test="success-message" isIncomplete={shouldShowWarning}>
        <Styled.MessageIcon
          name={shouldShowWarning ? 'exclamationPoint' : 'filledCheckmark'}
        />
        <Styled.MessageText>
          {shouldShowWarning
            ? 'Citation copied, but may be incomplete.'
            : `${text} copied`}
        </Styled.MessageText>
      </Styled.Message>
      <Styled.BoldHeader>Next, check your paper for</Styled.BoldHeader>
      <div role="list">
        <Styled.MessageText role="listitem">
          <Icon name="checkmark" color="green" />
          Accidental plagiarism
        </Styled.MessageText>
        {Config.enableGrammar && (
          <Styled.MessageText role="listitem">
            <Icon name="checkmark" color="green" />
            Grammar suggestions
          </Styled.MessageText>
        )}
      </div>
      <Styled.ButtonContainer>
        <Styled.Button data-test="check-paper-btn" onClick={onClickCheckPaper}>
          Check paper
        </Styled.Button>
      </Styled.ButtonContainer>
    </Modal>
  )
}

const mapStateToProps = (state) => ({
  modalData: modalDataSelector(state),
  modalName: modalTypeSelector(state),
})

const mapDispatchToProps = {
  closeModal,
  userClick,
}

export default connect(mapStateToProps, mapDispatchToProps)(CopySuccess)
